import format from "date-fns/format";
import { commercialRepOptions, commercialRepIds } from "../data/portalFormListData";
import { formatLegDepTimeDisplay } from "./formatLegDepTimeDisplay";

// Stage -> badge colour, matching the Zoho CRM Kanban palette
export const STAGE_COLOURS = {
    "Ready for quote": "#F7EDDA",
    "Out to market": "#DAE0F7",
    "Broker review": "#E2F7DA",
    "Contract requested": "#DAF5F7",
    "Contract signed": "#F7DAE4",
    "Payment initiated": "#E7DAF7",
    "Payment received": "#F7EADA",
    "Confirmed with operator": "#DAE7F7",
    Quoted: "#F7DAEF",
    "Contract sent": "#F7E2DA",
};
export const STAGE_COLOUR_DEFAULT = "#E5E7EB";

export const STAGE_ORDER = Object.keys(STAGE_COLOURS);

export const STAGE_GROUPS = [
    {
        key: "action",
        label: "Action Required",
        stages: ["Ready for quote", "Out to market"],
    },
    {
        key: "awaiting",
        label: "Awaiting Feedback",
        stages: [
            "Broker review",
            "Contract requested",
            "Payment initiated",
            "Contract signed",
            "Payment received",
            "Confirmed with operator",
            "Quoted",
            "Contract sent",
        ],
    },
];

// Normalise a stage label for tolerant matching (lowercase, alphanumerics only).
export const normaliseStage = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const STAGE_COLOUR_INDEX = Object.entries(STAGE_COLOURS).reduce((acc, [label, hex]) => {
    acc[normaliseStage(label)] = hex;
    return acc;
}, {});

export const getStageColour = (stage) => STAGE_COLOUR_INDEX[normaliseStage(stage)] || STAGE_COLOUR_DEFAULT;

export const getDealStage = (deal) => deal?.stage ?? deal?.stageLabel ?? deal?.Stage ?? "";

export const dealStageInList = (deal, stages) => {
    const dealKey = normaliseStage(getDealStage(deal));
    return stages.some((stage) => normaliseStage(stage) === dealKey);
};

export const CHECKLIST_STAGES = STAGE_GROUPS.flatMap((group) => group.stages);

export const isChecklistStage = (deal) => dealStageInList(deal, CHECKLIST_STAGES);

export const filterChecklistDeals = (deals) => (Array.isArray(deals) ? deals : []).filter(isChecklistStage);

export const normalizeDealsResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === "object") {
        if (Array.isArray(data.deals)) return data.deals;
        if (Array.isArray(data.data)) return data.data;
    }
    return [];
};

// ── Value helpers ────────────────────────────────────────────────────────────

const dash = (v) => (v === 0 ? "0" : v ? String(v) : "–");

export const toYesNoDisplay = (v) => {
    if (v === true || (typeof v === "string" && v.toLowerCase() === "true")) return "Yes";
    if (v === false || (typeof v === "string" && v.toLowerCase() === "false")) return "No";
    if (v === "Yes" || v === "No") return v;
    return null;
};

export const serializeEditablePayload = (config, value) => {
    if (config.valueType === "yesNo") {
        if (value === "Yes") return true;
        if (value === "No") return false;
    }
    if (config.valueType === "commercialRep") {
        const id = commercialRepIds[value];
        return id ? { id } : value;
    }
    return value;
};

// ── Date helpers ───────────────────────────────────────────────────────────

const parseLegDate = (d) => {
    if (!d) return null;
    if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}/.test(d.trim())) {
        const [y, m, day] = d.trim().slice(0, 10).split("-");
        return new Date(Number(y), Number(m) - 1, Number(day));
    }
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? null : dt;
};

const parseDateTime = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? null : dt;
};

const formatDurationSince = (fromDate) => {
    const from = parseDateTime(fromDate);
    if (!from) return "–";

    const diffMs = Math.max(0, Date.now() - from.getTime());
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    return `${days}d ${hours}h ${minutes}m`;
};

// Sorted legs (defensive copy).
const sortedLegs = (deal) =>
    Array.isArray(deal.legs) ? [...deal.legs].sort((a, b) => (a.legNumber < b.legNumber ? -1 : 1)) : [];

// Leg 1 departure date and time — used for sorting (earliest to latest).
export const getDealTravelDate = (deal) => {
    const legs = sortedLegs(deal);
    const leg1 = legs.find((leg) => leg.legNumber === 1) ?? legs[0];
    if (!leg1) return null;
    const date = parseLegDate(leg1.legDate);
    if (!date || Number.isNaN(date.getTime())) return null;
    const time = String(leg1.legTime || "")
        .trim()
        .match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (time && Number(time[1]) < 24 && Number(time[2]) < 60 && Number(time[3] || 0) < 60) {
        date.setHours(Number(time[1]), Number(time[2]), Number(time[3] || 0), 0);
    }
    return date;
};

export const compareDealsByLeg1Date = (a, b) => {
    const da = getDealTravelDate(a);
    const db = getDealTravelDate(b);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da - db;
};

// ── Sorting ───────────────────────────────────────────────────────────────────
// Accessors keyed by column key. Dates return a Date (compared numerically);
// everything else returns a string (compared with locale/numeric awareness).
const SORT_ACCESSORS = {
    rfq: (d) => parseDateTime(d.dateTime_Ready_For_Quote),
    client: (d) => getClientName(d),
    legDates: (d) => getDealTravelDate(d),
    broker: (d) => getBrokerFilterValue(d),
    stage: (d) => getDealStage(d),
    commercialRep: (d) => getCommercialRep(d),
};

// Column keys that expose a sortable header.
export const SORTABLE_COLUMN_KEYS = Object.keys(SORT_ACCESSORS);

const isNilSortValue = (v) => v == null || v === "" || v === "–";

const compareSortValues = (a, b) => {
    const aNil = isNilSortValue(a);
    const bNil = isNilSortValue(b);
    if (aNil && bNil) return 0;
    if (aNil) return 1; // empty values sort last
    if (bNil) return -1;
    if (a instanceof Date && b instanceof Date) return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

export const makeDealComparator = (sortKey, sortDir = "asc") => {
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return compareDealsByLeg1Date;
    const factor = sortDir === "desc" ? -1 : 1;
    return (a, b) => {
        const res = compareSortValues(accessor(a), accessor(b));
        if (res !== 0) return res * factor;
        return compareDealsByLeg1Date(a, b);
    };
};

const needsBrokerFeedbackAction = (deal) => dealStageInList(deal, ["Broker review"]) && hasUnresolvedFeedback(deal);

const stageRank = (deal) => {
    const rank = STAGE_ORDER.findIndex((stage) => normaliseStage(stage) === normaliseStage(getDealStage(deal)));
    return rank < 0 ? STAGE_ORDER.length : rank;
};

const compareByStageThenDeparture = (a, b) => stageRank(a) - stageRank(b) || compareDealsByLeg1Date(a, b);

export const groupDealsByStage = (deals, sort) => {
    const comparator = sort && sort.key ? makeDealComparator(sort.key, sort.dir) : compareByStageThenDeparture;
    return STAGE_GROUPS.map((group) => ({
        ...group,
        deals: deals
            .filter((deal) =>
                needsBrokerFeedbackAction(deal) ? group.key === "action" : dealStageInList(deal, group.stages)
            )
            .sort(
                (a, b) =>
                    Number(needsBrokerFeedbackAction(b)) - Number(needsBrokerFeedbackAction(a)) || comparator(a, b)
            ),
    }));
};

// Stacked per-leg departure dates — one entry per leg (e.g. ["05/07/2026", "07/07/2026"]).
const getLegDatesDisplay = (deal) => {
    const legs = sortedLegs(deal);
    if (!legs.length) return ["–"];
    return legs.map((leg) => {
        const dt = parseLegDate(leg.legDate);
        return dt ? format(dt, "dd/MM/yyyy") : "–";
    });
};

// Stacked per-leg departure times — one entry per leg (e.g. ["14:30", "09:00"]).
const getLegTimesDisplay = (deal) => {
    const legs = sortedLegs(deal);
    if (!legs.length) return ["–"];
    return legs.map((leg) => (leg.legTime ? formatLegDepTimeDisplay(leg.legTime) : "–"));
};

// Continuous path (e.g. "EGSS–LSGS–EGSS"), split into lines of up to 3 airports.
const getRouteDisplay = (deal) => {
    const legs = sortedLegs(deal);
    if (!legs.length) return ["–"];
    const points = [];
    legs.forEach((leg, i) => {
        if (i === 0 && leg.legFrom) points.push(leg.legFrom);
        if (leg.legTo) points.push(leg.legTo);
    });
    if (!points.length) return ["–"];

    const lines = [];
    for (let i = 0; i < points.length; i += 3) {
        lines.push(points.slice(i, i + 3).join("–"));
    }
    return lines;
};

const getClientName = (deal) => {
    if (deal.leadFullName?.trim()) return deal.leadFullName.trim();
    if (deal.contactFullName?.trim()) return deal.contactFullName.trim();
    if (deal.deal_Name?.trim()) return deal.deal_Name.trim();
    return "–";
};

// Split a full name so the first name sits on line one and the rest on line two
// (rendered as a stacked cell).
const splitNameLines = (name) => {
    const n = String(name ?? "").trim();
    if (!n || n === "–") return ["–"];
    const [first, ...rest] = n.split(/\s+/);
    return rest.length ? [first, rest.join(" ")] : [first];
};

const getClientNameDisplay = (deal) => splitNameLines(getClientName(deal));

// First name on line one, remaining names on line two (rendered as a stacked cell).
const getBroker = (deal) => splitNameLines(getBrokerFilterValue(deal));

export const getBrokerFilterValue = (deal) => String(deal.broker ?? deal.ownerFullName ?? "").trim();

export const distinctBrokers = (deals) =>
    [...new Set((deals || []).map(getBrokerFilterValue).filter(Boolean))].sort((a, b) => a.localeCompare(b));

// Avinode Trip ID (used as link to the Avinode trip)
const getTripId = (deal) => dash(deal.avinodeTripId);

// Sales notes captured on the flight request.
const getAdditionalNotes = (deal) => dash(deal.notes_on_Flight);

// Date on the first line, time on the second (rendered as a stacked cell).
const getRfqDateTime = (deal) => {
    const dt = parseDateTime(deal.dateTime_Ready_For_Quote);
    return dt ? [format(dt, "dd/MM/yyyy"), format(dt, "HH:mm")] : ["–"];
};

const getLegPaxDisplay = (deal) => {
    const legs = sortedLegs(deal);
    if (!legs.length) return [dash(deal.legPax)];
    return legs.map((leg) => dash(leg.legPax));
};

const getLegLuggageDisplay = (deal) => {
    const legs = sortedLegs(deal);
    if (!legs.length) return [dash(deal.legLuggage)];
    return legs.map((leg) => dash(leg.legLuggage));
};

const getPetsDisplay = (deal) => dash(deal.number_of_Pets);

const getDurationInStage = (deal) => formatDurationSince(deal.stage_date);

export const getCommercialRep = (deal) => dash(deal.commercialExecAssigned ?? deal.ownerFullName);

const toRepName = (v) => {
    if (v == null) return "";
    if (typeof v === "string") return v.trim();
    if (typeof v === "object") return String(v.name ?? v.full_Name ?? v.fullName ?? "").trim();
    return String(v).trim();
};

export const resolveCommercialRepName = (deal) =>
    toRepName(deal?.commercialExecAssigned) || toRepName(deal?.commercial_Exec_used) || null;

// A "new" deal is one nobody has picked up yet — i.e. no Commercial Rep assigned.
// Deliberately uses resolveCommercialRepName (not getCommercialRep, which falls
// back to the deal owner) so the bell only counts genuinely unassigned deals.
export const isUnassignedDeal = (deal) => {
    const rep = resolveCommercialRepName(deal);
    return !rep || rep === "–";
};

export const countUnassignedDeals = (deals) => (Array.isArray(deals) ? deals.filter(isUnassignedDeal).length : 0);

export const getDealPriority = (deal) => {
    const flag = deal.highPriority ?? deal.high_Priority ?? deal.commercialPriority ?? deal.priority;
    if (typeof flag === "boolean") return flag;
    if (typeof flag === "string") return ["high", "yes", "true", "1"].includes(flag.toLowerCase());
    return false;
};

// ── Column definitions ───────────────────────────────────────────────────────
export const COMMERCIAL_COLUMNS = [
    { key: "rfq", label: "RFQ Date & Time", accessor: getRfqDateTime, isStacked: true, sortable: true },
    { key: "client", label: "Client / Lead", accessor: getClientNameDisplay, isStacked: true, sortable: true },
    { key: "route", label: "Route", accessor: getRouteDisplay, isDealLink: true },
    { key: "pax", label: "Pax", accessor: getLegPaxDisplay, isStacked: true },
    { key: "luggage", label: "Luggage", accessor: getLegLuggageDisplay, isStacked: true },
    { key: "pets", label: "Pets", accessor: getPetsDisplay },
    { key: "legDates", label: "Leg Date(s)", accessor: getLegDatesDisplay, isStacked: true, sortable: true },
    { key: "legTimes", label: "Leg Time(s)", accessor: getLegTimesDisplay, isStacked: true },
    {
        key: "additionalNotes",
        label: "Additional Notes",
        accessor: getAdditionalNotes,
        editable: { type: "textarea", maxLength: 2000, dealKey: "notes_on_Flight", payloadKey: "Notes_on_Flight" },
    },
    { key: "broker", label: "Broker", accessor: getBroker, isStacked: true, sortable: true },
    {
        key: "uplift",
        label: "Uplift",
        accessor: (d) => dash(d.uplift),
        editable: { type: "text", dealKey: "uplift", payloadKey: "Uplift" },
    },
    {
        key: "shopped",
        label: "Shopped?",
        accessor: (d) => dash(toYesNoDisplay(d.shopped)),
        editable: {
            type: "select",
            options: ["Yes", "No"],
            dealKey: "shopped",
            payloadKey: "Shopped",
            valueType: "yesNo",
        },
    },
    { key: "tripId", label: "Trip ID", accessor: getTripId, isAvinode: true },
    { key: "stage", label: "Stage", accessor: (d) => getDealStage(d) || "–", isStatus: true, sortable: true },
    {
        key: "commercialRep",
        label: "Commercial Rep",
        accessor: getCommercialRep,
        sortable: true,
        editable: {
            type: "select",
            options: commercialRepOptions,
            dealKey: "commercialExecAssigned",
            payloadKey: "Commercial_Exec_used",
            valueType: "commercialRep",
        },
    },
    {
        key: "commercialNotes",
        label: "Commercial Notes",
        accessor: (d) => dash(d.commercial_Notes),
        editable: { type: "textarea", maxLength: 2000, dealKey: "commercial_Notes", payloadKey: "Commercial_Notes" },
    },
    { key: "feedback", label: "Feedback", accessor: (d) => d.feedbackSummary, isFeedback: true },
    { key: "durationInStage", label: "Duration in Stage", accessor: getDurationInStage },
];

// ── Filtering ─────────────────────────────────────────────────────────────────

export const FEEDBACK_FILTER_UNRESOLVED = "Unresolved feedback";
export const FEEDBACK_FILTER_OPTIONS = [FEEDBACK_FILTER_UNRESOLVED];

export const hasUnresolvedFeedback = (deal) => (deal?.feedbackSummary?.unresolvedCount || 0) > 0;

export const filterDeals = (deals, { search, statuses, commercials, brokers, feedback }) => {
    if (!Array.isArray(deals)) return [];
    const q = (search || "").trim().toLowerCase();

    return deals.filter((deal) => {
        if (q) {
            const client = getClientName(deal).toLowerCase();
            const avinode = String(deal.avinodeTripId || "").toLowerCase();
            if (!client.includes(q) && !avinode.includes(q)) return false;
        }

        if (statuses && statuses.length && !statuses.includes(getDealStage(deal))) return false;

        if (commercials && commercials.length) {
            const rep = deal.commercialExecAssigned ?? deal.ownerFullName;
            if (!commercials.includes(rep)) return false;
        }

        if (brokers && brokers.length && !brokers.includes(getBrokerFilterValue(deal))) return false;

        if (feedback && feedback.includes(FEEDBACK_FILTER_UNRESOLVED) && !hasUnresolvedFeedback(deal)) return false;

        return true;
    });
};

// Split into the two stacked sections; sort each by leg-1 date ascending.
export const splitByPriority = (deals) => ({
    priority: deals.filter(getDealPriority).sort(compareDealsByLeg1Date),
    standard: deals.filter((d) => !getDealPriority(d)).sort(compareDealsByLeg1Date),
});

// Distinct, defined values for a field across the deal set (for filter options).
export const distinctValues = (deals, key) =>
    [...new Set((deals || []).map((d) => d[key]).filter((v) => v != null && v !== ""))].sort();
